/**
 * ENG-3429 — one-time secrets this app is opened with, kept out of every analytics tag.
 *
 * A password-reset email links to `/?mode=resetPassword&oobCode=…` (Firebase's own action-link format
 * can add `apiKey` and `continueUrl`), and the code works until it is used or expires. GA4 records the
 * page URL, query string included, and so does Google Ads — each loaded twice: by public/index.html,
 * and again by tags inside GTM-PGVN9588, which this page cannot configure. So the FIRST script in
 * public/index.html takes these params out of the address bar before any tag loads, and keeps them in
 * `window.__havenUrlSecrets` for this page to read. They are therefore NOT in `window.location` — read
 * them here.
 *
 * Out of the address bar also keeps them out of the next page's referrer, which GA4 records too. The
 * cost is a reload of the reset page: nothing stores the code, so the reload has none, and
 * ResetPasswordPage says to open the emailed link again. Do not "fix" that by persisting the code.
 *
 * ENG-3430: three more params leak the same way, and go the same way.
 * - `confirmAlias`: the alias-confirmation token. It works without signing in, so anyone who can read
 *   the analytics could confirm the alias. A reload of that page has no token and shows sign-in.
 * - `join`: the invitation token. useJoinInvitation already saves it for a week, so a reload keeps it.
 * - `email`: an address to prefill sign-up with, which is personal data in analytics.
 *
 * ENG-3832: `claim`, a demo org claim link's one-time token. It arrives as the whole hash
 * (`#claim=<token>`) rather than a query param, and index.html takes it from there; whoever holds it
 * can create the org it names. lib/orgClaim saves it so it survives signing in.
 */

/** The params public/index.html moves out of the URL. `urlSecrets.test.ts` holds the two lists together. */
export const URL_SECRETS = ["oobCode", "apiKey", "continueUrl", "confirmAlias", "join", "email", "claim"] as const;
export type UrlSecret = (typeof URL_SECRETS)[number];

declare global {
  interface Window {
    /** Set by the first script in public/index.html. Only the secrets the URL carried. */
    __havenUrlSecrets?: Partial<Record<UrlSecret, string>>;
    /** Set by that script when it could not clean the URL; every analytics tag then stays off. */
    __havenNoAnalytics?: boolean;
  }
}

/**
 * A secret this page was opened with. Falls back to the query string, where the secret still is when
 * that script did not run (unit tests) or could not remove it (no analytics loaded then).
 */
export function readUrlSecret(name: UrlSecret): string | null {
  const stashed = window.__havenUrlSecrets?.[name];
  if (typeof stashed === "string") return stashed;
  return new URLSearchParams(window.location.search).get(name);
}

/**
 * Forget a secret once the app is done with it, as taking it out of the URL used to. Without this, App
 * mounting again (the root error boundary's retry) would pick up an invitation the member already
 * accepted or turned down.
 */
export function forgetUrlSecret(name: UrlSecret): void {
  if (window.__havenUrlSecrets) delete window.__havenUrlSecrets[name];
}

/** Whether this page is at a password-reset link, whether or not it still has the code (a reload has not). */
export function isPasswordResetLink(): boolean {
  return new URLSearchParams(window.location.search).get("mode") === "resetPassword";
}

/** The code from a password-reset link, or null when this page has none to use. */
export function passwordResetCode(): string | null {
  if (!isPasswordResetLink()) return null;
  return readUrlSecret("oobCode") || null;
}

/** Whether this page is at an email-verification link (ENG-4080), whether or not it still has the code. */
export function isVerifyEmailLink(): boolean {
  return new URLSearchParams(window.location.search).get("mode") === "verifyEmail";
}

/** The code from an email-verification link, or null when this page has none to use (a reload). */
export function verifyEmailCode(): string | null {
  if (!isVerifyEmailLink()) return null;
  return readUrlSecret("oobCode") || null;
}
